import React, { useState } from 'react';
import { Row, Col, Card, Tabs, Tab, Button } from 'react-bootstrap';
import { EmployeeOptionsStatement as EmployeeOptionsData, TabType } from '../types/employee-statement';
import { ActivityTable } from './ActivityTable';
import { SummaryTable } from './SummaryTable';
import IntegerFmt from '../../components/common/typeFormatting/IntegerFmt';
import Dollars from '../../components/common/typeFormatting/Dollars';

interface EmployeeOptionsStatementProps {
  data: EmployeeOptionsData;
  isLoading?: boolean;
  onExerciseClick?: () => void;
  showEmployeeLabel?: boolean;
}

export const EmployeeOptionsStatement = ({
  data,
  isLoading = false,
  onExerciseClick,
  showEmployeeLabel = false
}: EmployeeOptionsStatementProps) => {
  const [activeTab] = useState<TabType>(TabType.SUMMARY);

  if (isLoading) {
    return <div>Loading employee options...</div>;
  }

  if (!data.hasAnyOptions) {
    return (
      <div className="text-center p-4">
        <h5>No options available for this employee.</h5>
      </div>
    );
  }

  // Calculate totals from data
  const calculateTotals = () => {
    if (!data || !data.activeOptions.options.length) {
      return {
        totalOptions: 0,
        vestedOptions: 0,
        vestedWithPositiveValue: 0,
        currentPrice: 0,
        cashlessEarnings: 0
      };
    }

    const options = data.activeOptions.options;
    const totalExercised = options.reduce((sum, opt) => sum + opt.exercisedShares, 0);
    const totalOptions = options.reduce((sum, opt) => sum + opt.totalShares, 0) - totalExercised;
    const vestedOptions = options.reduce((sum, opt) => sum + opt.vestedShares, 0) - totalExercised;
    const vestedWithPositiveValue = options
      .filter(opt => opt.priceDifference > 0)
      .reduce((sum, opt) => sum + opt.vestedValue, 0);
    const currentPrice = options.length > 0 ? options[0].currentPrice : 0;
    const cashlessEarnings = options.reduce((sum, opt) => sum + opt.cashlessProceeds, 0);

    return {
      totalOptions,
      vestedOptions,
      vestedWithPositiveValue,
      currentPrice,
      cashlessEarnings
    };
  };

  const totals = calculateTotals();
  const hasCashlessEarnings = totals.cashlessEarnings > 0;

  // Only show tabs that have data
  const availableTabs = [
    { key: TabType.SUMMARY, label: 'Summary', hasData: data.hasAnyOptions },
    { key: TabType.ACTIVITY, label: 'Activity', hasData: true }
  ].filter(tab => tab.hasData);

  // Set the first available tab as active if current tab has no data
  const currentTab = availableTabs.find(tab => tab.key === activeTab)
    ? activeTab
    : availableTabs[0]?.key || TabType.SUMMARY;

  const renderTabContent = (tabKey: TabType) => {
    switch (tabKey) {
      case TabType.SUMMARY:
        return <SummaryTable data={data} />;
      case TabType.ACTIVITY:
        return <ActivityTable data={data} />;
      default:
        return <div>No data available.</div>;
    }
  };

  return (
    <div className="employee-options-statement">
      {/* Summary Panel */}
      <Row>
        <Col>
          <Card style={{marginTop: 25, borderRadius: '16px', background: '#fff', border: '1px solid #dee2e6'}}>
            <Card.Body style={{padding: '1rem'}}>
              {showEmployeeLabel && (
                <div style={{ textAlign: 'left', marginBottom: '1rem' }}>
                  <h5 style={{ margin: 0, fontWeight: 600 }}>{data.employee.fullName}'s Options Statement</h5>
                </div>
              )}
              <Row>
                <Col md={hasCashlessEarnings ? 2 : 3}>
                  <div style={{ textAlign: 'center', padding: '0.5rem' }}>
                    <div style={{ fontSize: '0.875rem', color: '#546e7a', marginBottom: '0.5rem', minHeight: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Total Options Remaining</div>
                    <div style={{ fontSize: '1.75rem', fontWeight: 600, color: '#000' }}>
                      <IntegerFmt>{totals.totalOptions}</IntegerFmt>
                    </div>
                  </div>
                </Col>
                <Col md={hasCashlessEarnings ? 2 : 3}>
                  <div style={{ textAlign: 'center', padding: '0.5rem' }}>
                    <div style={{ fontSize: '0.875rem', color: '#546e7a', marginBottom: '0.5rem', minHeight: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Vested Options Remaining</div>
                    <div style={{ fontSize: '1.75rem', fontWeight: 600, color: '#000' }}>
                      <IntegerFmt>{totals.vestedOptions}</IntegerFmt>
                    </div>
                  </div>
                </Col>
                <Col md={hasCashlessEarnings ? 3 : 3}>
                  <div style={{ textAlign: 'center', padding: '0.5rem' }}>
                    <div style={{ fontSize: '0.875rem', color: '#546e7a', marginBottom: '0.5rem', minHeight: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Vested Options w/ Positive Value</div>
                    <div style={{ fontSize: '1.75rem', fontWeight: 600, color: '#000' }}>
                      <Dollars>{totals.vestedWithPositiveValue}</Dollars>
                    </div>
                  </div>
                </Col>
                {hasCashlessEarnings && (
                  <Col md={3}>
                    <div style={{ textAlign: 'center', padding: '0.5rem' }}>
                      <div style={{ fontSize: '0.875rem', color: '#546e7a', marginBottom: '0.5rem', minHeight: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Earnings to Date</div>
                      <div style={{ fontSize: '1.75rem', fontWeight: 600, color: '#000' }}>
                        <Dollars>{totals.cashlessEarnings}</Dollars>
                      </div>
                    </div>
                  </Col>
                )}
                <Col md={2}>
                  <div style={{ textAlign: 'center', padding: '0.5rem' }}>
                    <div style={{ fontSize: '0.875rem', color: '#546e7a', marginBottom: '0.5rem', minHeight: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Current Price</div>
                    <div style={{ fontSize: '1.75rem', fontWeight: 600, color: '#000' }}>
                      <Dollars>{totals.currentPrice}</Dollars>
                    </div>
                  </div>
                </Col>
              </Row>
              {/* Exercise Options Button */}
              {onExerciseClick && (
                <Row className="mt-3">
                  <Col className="text-right">
                    <Button variant="primary" onClick={onExerciseClick}>
                      Exercise Options
                    </Button>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Navigation Tabs */}
      {availableTabs.length > 1 && (
        <Row>
          <Col>
            <Card style={{marginTop: 25}}>
              <Card.Body style={{minHeight: "600px"}}>
                <Tabs defaultActiveKey={currentTab} id="employee-options-tabs" className="mb-3" style={{borderBottom: "1px solid #dee2e6"}}>
                  {availableTabs.map(tab => (
                    <Tab key={tab.key} eventKey={tab.key} title={tab.label}>
                      {renderTabContent(tab.key)}
                    </Tab>
                  ))}
                </Tabs>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Single Tab Content (when only one tab) */}
      {availableTabs.length === 1 && (
        <Row>
          <Col>
            <Card style={{marginTop: 25}}>
              <Card.Body style={{minHeight: "600px"}}>
                {renderTabContent(availableTabs[0].key)}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};